import { ValidationError } from '@eliadmin/core/src/api/errors';
import { askAboutProduct } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  useDialogStore,
} from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { env } from '@eliadmin/core/src/env';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { isServer } from '@eliadmin/core/src/utils/misc-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';

import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, boolean as __eliadmin_zod_2, number as __eliadmin_zod_3 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { TbHelp } from 'react-icons/tb';
import { LuX } from 'react-icons/lu';

export const ASK_FOR_PRODUCT_DIALOG_ID = 'ASK_FOR_PRODUCT_DIALOG_ID';

const askAboutProductSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    email: __eliadmin_zod_1().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    text: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    gdpr: __eliadmin_zod_2().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }),
    product: __eliadmin_zod_0({
      id: __eliadmin_zod_3(),
    }),
    product_url: __eliadmin_zod_1().url(),
    ['g-recaptcha-response']: __eliadmin_zod_1().optional(),
  });

export type AskAboutProductFormValues = z.infer<ReturnType<typeof askAboutProductSchema>>;

export function ProductAskDialog({ title }: { title: string }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);

  const product = useVariantsStore(s => s.product);
  const me = useCustomerStore(s => s.me?.customer);

  const askMutation = useMutation({ mutationFn: askAboutProduct });

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof askAboutProductSchema>>>({
    resolver: zodResolver(askAboutProductSchema(formValidationMessages)),
    defaultValues: {
      product: {
        id: product.id,
      },
      email: me?.email ?? '',
      text: '',
      gdpr: false,
      product_url: isServer ? `${env.NEXT_PUBLIC_APP_URL}/${product.slug}` : window.location.href,
    },
  });
  const close = useDialogStore(state => state.close);

  const handleSubmit = form.handleSubmit(async data => {
    try {

      await askMutation.mutateAsync({
        locale,
        data: {
          ...data,

        },
      });
      toast({
        title: t('text659'),
        variant: 'success',
      });
      close(ASK_FOR_PRODUCT_DIALOG_ID);
    } catch (err) {
      // handle 422
      if (err instanceof ValidationError) {
        for (const [field, errors] of Object.entries(err.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text660'),
        description: (err as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <Dialog>
        <DialogTrigger className="flex items-center justify-start gap-2 transition-all hover:opacity-80">
          <TbHelp size={24} className="text-primary" />
          <p className="text-sm font-bold uppercase tracking-tight text-primary underline">
            {t('text446')}
          </p>
        </DialogTrigger>
        <DialogContent
          hideClose
          overlayClassName="bg-black/30 backdrop-blur-none"
          className="w-full max-w-[670px] rounded-[16px] bg-[#F2EDE6] sm:max-w-[670px]"
        >
          <form
            onSubmit={handleSubmit}
            className={cn(
              'flex flex-col items-start justify-start gap-8',
              'ea-product-detail-ask-dialog',
            )}
          >
            <DialogHeader
              className={cn(
                'flex w-full flex-row items-start justify-between',
                'ea-product-detail-ask-dialog__header',
              )}
            >
              <DialogTitle className={cn('h3', 'ea-product-detail-ask-dialog__header-title')}>
                {title}
              </DialogTitle>
              <DialogClose className="transition-all hover:opacity-75">
                <LuX size={24} className="text-muted" />
              </DialogClose>
            </DialogHeader>
            <div
              className={cn(
                'flex w-full flex-col items-start justify-start gap-4',
                'ea-product-detail-ask-dialog__content',
              )}
            >
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem
                    required
                    className={cn('w-full', 'ea-product-detail-ask-dialog__form-item-email')}
                  >
                    <FormControl
                      className={cn(
                        'w-full',
                        'ea-product-detail-ask-dialog__form-item-email-control',
                      )}
                    >
                      <Input
                        {...field}
                        placeholder={t('text35')}
                        className={{
                          wrapper: cn(
                            'w-full',
                            'ea-product-detail-ask-dialog__form-item-email-input-wrapper',
                          ),
                          main: cn('', 'ea-product-detail-ask-dialog__form-item-email-input'),
                        }}
                      />
                    </FormControl>
                    <FormMessage
                      className={cn('', 'ea-product-detail-ask-dialog__form-item-email-message')}
                    />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="text"
                render={({ field }) => (
                  <FormItem
                    className={cn('w-full', 'ea-product-detail-ask-dialog__form-item-message')}
                    required
                  >
                    <FormControl
                      className={cn(
                        'w-full',
                        'ea-product-detail-ask-dialog__form-item-message-control',
                      )}
                    >
                      <Textarea
                        {...field}
                        placeholder={t('text101')}
                        className={cn(
                          'w-full rounded-[8px]',
                          'ea-product-detail-ask-dialog__form-item-message-textarea',
                        )}
                      />
                    </FormControl>
                    <FormMessage
                      className={cn('', 'ea-product-detail-ask-dialog__form-item-message-message')}
                    />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="gdpr"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex flex-row items-start space-x-3 space-y-0',
                    'ea-product-detail-ask-dialog__form-item-condition-accepted',
                  )}
                >
                  <FormControl
                    className={cn(
                      '',
                      'ea-product-detail-ask-dialog__form-item-condition-accepted-control',
                    )}
                  >
                    <Checkbox
                      ref={field.ref}
                      name={field.name}
                      disabled={field.disabled}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className={cn(
                        '',
                        'ea-product-detail-ask-dialog__form-item-condition-accepted-checkbox',
                      )}
                    />
                  </FormControl>
                  <FormLabel
                    className={cn(
                      'pt-1',
                      'ea-product-detail-ask-dialog__form-item-condition-accepted-labes',
                    )}
                  >
                    {t('text264')}
                    &nbsp;
                    <Link
                      href={t('text391') ?? '#'}
                      target="_blank"
                      className={cn(
                        'underline',
                        'ea-product-detail-ask-dialog__form-item-condition-accepted-link1',
                      )}
                      prefetch={false}
                    >
                      {t('text265')}
                    </Link>
                    ,&nbsp;
                    <Link
                      href={t('text392') ?? '#'}
                      target="_blank"
                      className={cn(
                        'underline',
                        'ea-product-detail-ask-dialog__form-item-condition-accepted-link2',
                      )}
                      prefetch={false}
                    >
                      {t('text436')}
                    </Link>
                  </FormLabel>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'g-recaptcha-response'}
              render={() => (
                <FormItem required className={cn('hidden w-full')}>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className={cn('w-full', 'ea-product-detail-ask-dialog__footer')}>
              <Button
                type="submit"
                className={cn('w-full sm:w-fit', 'ea-product-detail-ask-dialog__footer-button')}
                disabled={!form.formState.isValid || form.formState.isSubmitting}
                isLoading={form.formState.isSubmitting || form.formState.isSubmitSuccessful}
              >
                {t('text185')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
}
